import type { CascaderValue, IBuildStackFrame, ICascaderItem } from './types';

export function createNode(
  node: ICascaderItem,
  parent: ICascaderItem | null,
): ICascaderItem {
  return {
    ...node,
    parent,
    children: [],
  };
}

export function clone<T extends ICascaderItem>(
  from: ICascaderItem[],
  cloneNode: (node: ICascaderItem, parent: T) => T,
): T[] {
  const stack: IBuildStackFrame<T>[] = from?.map((n) => ({
    node: cloneNode(n, null),
    children: n?.children,
  }));
  const trees = stack.map((n) => n.node);

  while (stack.length > 0) {
    const state = stack.pop();
    if (!state) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const { node, children } = state || {};
    children?.forEach((n) => {
      const m = cloneNode(n, node);
      stack.push({ node: m, children: n?.children });
      node?.children?.push(m);
    });
  }

  return trees;
}

/**
 * 判断树的两个路径是否相同
 */
export function isPathEqual(x: ICascaderItem[], y: ICascaderItem[]): boolean {
  if (x.length !== y.length) {
    return false;
  }

  for (let i = 0; i < x.length; i++) {
    if (x[i].adCode !== y[i].adCode) {
      return false;
    }
  }

  return true;
}

/**
 * 合并树的路径
 */
export function union(
  x: ICascaderItem[][],
  y: ICascaderItem[][],
): ICascaderItem[][] {
  return y.reduce(
    (acc, path) => {
      const match = acc.some((i) => isPathEqual(i, path));
      if (!match) {
        acc.push(path);
      }

      return acc;
    },
    [...x],
  );
}

/**
 * 两个树路径集合的差集
 */
export function difference(
  x: ICascaderItem[][],
  y: ICascaderItem[][],
): ICascaderItem[][] {
  return x.filter((i) => y.every((j) => !isPathEqual(i, j)));
}

/**
 * 从根节点到 `node` 的路径
 */
export function getPathToNode(node: ICascaderItem): ICascaderItem[] {
  let parent: ICascaderItem | null | undefined = node;
  const path: ICascaderItem[] = [];

  while (parent) {
    path.unshift(parent);
    parent = parent.parent;
  }

  return path;
}

/**
 * 获取路径的 label 描述
 */
export const getPathLabel = (path: ICascaderItem[]): string =>
  path.map((it) => it.name).join(' / ');

/**
 * 获取路径的 value 描述
 */
export const getPathValue = (path: ICascaderItem[]): string =>
  path.map((it) => it.adCode).join('-');

/**
 * 查找树中某个节点的子节点
 */
export function getNodeChildren(
  options: ICascaderItem[] | null | undefined,
  value: unknown,
): ICascaderItem[] | null {
  if (options && options.length > 0) {
    const currOptions = options.find((opt) => opt.adCode === value);
    if (currOptions && Array.isArray(currOptions.children)) {
      return currOptions.children;
    }
  }

  return null;
}

export function getPathByValue(
  options: ICascaderItem[],
  values: CascaderValue[],
) {
  const selectedPaths: ICascaderItem[] = [];
  const length = values.length;

  for (
    let i = 0, opts: ICascaderItem[] | null | undefined = options;
    i < length;
    i++, opts = getNodeChildren(opts, values[i - 1])
  ) {
    if (opts && opts.length > 0) {
      const val = values[i];
      const selectedItem = opts.find((n) => n.adCode === val);

      if (selectedItem) selectedPaths.push(selectedItem);
    }
  }

  return selectedPaths;
}

/**
 * Returns node depth, root node has depth 1.
 */
export function getNodeDepth(node: ICascaderItem): number {
  let depth = 1;
  let { parent } = node;

  while (parent) {
    parent = parent.parent;
    depth++;
  }
  return depth;
}
